import { getUrlFriendlyString, hasValue, simpleHash } from '@utils';
const CACHE_PREFIX = 'lucy_cache_';
const DEFAULT_CACHE_TIME = 5; // 5 minutes
const DEFAULT_STORE = 'memory';
export class CacheManager {
    constructor() {
        // In-memory backend (the default). localStorage is the 'local' backend.
        this.memory = new Map();
    }
    static getInstance() {
        if (!CacheManager.instance) {
            CacheManager.instance = new CacheManager();
        }
        return CacheManager.instance;
    }
    /**
     * Generate cache key from action/service parameters
     */
    generateCacheKey(type, identifier, params) {
        const paramsHash = this.hashObject(params);
        return getUrlFriendlyString(`${type}_${identifier}_${paramsHash}`);
    }
    /**
     * Create a simple hash from object for cache key
     */
    hashObject(obj) {
        const str = JSON.stringify(obj, Object.keys(obj).sort());
        return simpleHash(str, null, { base: 36, ignorePrefix: true });
    }
    /**
     * Get cached data if available and not expired, from the given backend.
     */
    getCachedData(cacheKey, store = DEFAULT_STORE) {
        var _a;
        try {
            const parsed = store === 'memory'
                ? (_a = this.memory.get(cacheKey)) !== null && _a !== void 0 ? _a : null
                : this.readLocal(cacheKey);
            if (!parsed)
                return null;
            if (Date.now() > parsed.expiresAt) {
                this.removeCachedData(cacheKey, store); // expired — evict
                return null;
            }
            return parsed.data;
        }
        catch (error) {
            console.warn('Error reading from cache:', error);
            return null;
        }
    }
    readLocal(cacheKey) {
        const raw = localStorage.getItem(CACHE_PREFIX + cacheKey);
        return raw ? JSON.parse(raw) : null;
    }
    /**
     * Store data in the given backend.
     */
    setCachedData(cacheKey, data, cacheTimeMinutes = DEFAULT_CACHE_TIME, store = DEFAULT_STORE) {
        try {
            const now = Date.now();
            const cachedItem = {
                data,
                timestamp: now,
                expiresAt: now + (cacheTimeMinutes * 60 * 1000),
            };
            if (store === 'memory')
                this.memory.set(cacheKey, cachedItem);
            else
                localStorage.setItem(CACHE_PREFIX + cacheKey, JSON.stringify(cachedItem));
        }
        catch (error) {
            console.warn('Error storing in cache:', error);
        }
    }
    /**
     * Remove a specific entry. With no `store`, removes from both backends.
     */
    removeCachedData(cacheKey, store) {
        try {
            if (store !== 'local')
                this.memory.delete(cacheKey);
            if (store !== 'memory')
                localStorage.removeItem(CACHE_PREFIX + cacheKey);
        }
        catch (error) {
            console.warn('Error removing from cache:', error);
        }
    }
    /**
     * Clear ALL cached data across both backends (memory + localStorage lucy_cache_*).
     */
    clearAllCache() {
        try {
            this.memory.clear();
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(CACHE_PREFIX))
                    localStorage.removeItem(key);
            });
        }
        catch (error) {
            console.warn('Error clearing cache:', error);
        }
    }
    /**
     * Clean up expired entries in both backends.
     */
    cleanupExpiredCache() {
        try {
            const now = Date.now();
            for (const [key, item] of this.memory) {
                if (now > item.expiresAt)
                    this.memory.delete(key);
            }
            Object.keys(localStorage).forEach(key => {
                if (!key.startsWith(CACHE_PREFIX))
                    return;
                try {
                    const item = JSON.parse(localStorage.getItem(key) || '');
                    if (now > item.expiresAt)
                        localStorage.removeItem(key);
                }
                catch (_a) {
                    localStorage.removeItem(key); // corrupt entry
                }
            });
        }
        catch (error) {
            console.warn('Error cleaning up cache:', error);
        }
    }
    /**
     * Get cache statistics
     */
    getCacheStats() {
        try {
            const keys = Object.keys(localStorage);
            const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
            let totalSize = 0;
            cacheKeys.forEach(key => {
                const item = localStorage.getItem(key);
                if (item) {
                    totalSize += item.length;
                }
            });
            return {
                totalEntries: cacheKeys.length,
                totalSize
            };
        }
        catch (error) {
            console.warn('Error getting cache stats:', error);
            return { totalEntries: 0, totalSize: 0 };
        }
    }
}
/**
 * Utility function to check if caching should be used
 */
export function shouldUseCache(options) {
    return (options === null || options === void 0 ? void 0 : options.enableCache) === true;
}
/**
 * Get cache time in minutes, with fallback to default
 */
export function getCacheTime(options) {
    return hasValue(options === null || options === void 0 ? void 0 : options.cacheTime, true, true) ? options.cacheTime : DEFAULT_CACHE_TIME;
}
/**
 * Resolve the storage backend, defaulting to memory (never persists PII to disk).
 */
export function getCacheStore(options) {
    var _a;
    return (_a = options === null || options === void 0 ? void 0 : options.store) !== null && _a !== void 0 ? _a : DEFAULT_STORE;
}
/**
 * Get or generate cache key
 */
export function resolveCacheKey(options, type, identifier, params) {
    const cacheManager = CacheManager.getInstance();
    if (hasValue(options === null || options === void 0 ? void 0 : options.cacheKey)) {
        return options.cacheKey;
    }
    return cacheManager.generateCacheKey(type, identifier, params);
}
